import React from "react"
import ReactSelect from "react-select"
import * as styles from "./SupplierSignupForm.module.scss"
import CheckboxArray from "@components/objects/CheckboxArray/CheckboxArray"
import { featureOptions } from "@data/static"
import ReactSelectAlias from "@components/objects/ReactSelectAlias/ReactSelectAlias"

const NEXT_PUBLIC_ENV = process.env.NEXT_PUBLIC_ENV

const FormDetails = ({
  data,
  reactSelectStyles,
  styleMaxReached,
  serviceMaxReached,
  categoryMaxReached,
  handleChangeSelect,
  handleChangeCheckboxArray,
  handleChange,
  categoryOptions,
  styleOptions,
  serviceOptions,
  subServiceOptions,
  subServiceMaxReached,
}) => {
  const descriptionMaxWordCount = 150

  const wordCount = (text) => {
    if (text) {
      return text.split(" ").length
    } else {
      return 0
    }
  }

  const noOptionsMessageStyles = ({ inputValue }) => {
    return styleMaxReached ? `You can only select 3 options` : `No options`
  }

  const noOptionsMessageServices = ({ inputValue }) => {
    return serviceMaxReached ? `You can only select 1 option` : `No options`
  }

  const noOptionsMessageCategories = ({ inputValue }) => {
    return categoryMaxReached ? `You can only select 5 options` : `No options`
  }

  return (
    <section>
      <h3>Supplier details</h3>
      <p>
        Please fill out this section as accurately as possible. This section will help us match you with the most
        relevant audience possible, increasing the chances of you receiving more enquiries for your services.
      </p>
      <div className="description-container">
        <label htmlFor="description">Business Description*</label>
        <p>
          Describe your business profile in 150 words. Try and give users a feel for what you do, how you do it, why you
          do it, and why you are good at it! Feel free to reference past events you have done and any other random
          information that might spark their interest. Be sure to highlight the wonderful features that make your
          business unique!
        </p>
        <textarea
          id="description"
          name="description"
          required
          onChange={handleChange}
          value={data.description}
        ></textarea>
        <div>
          {wordCount(data.description)} / {descriptionMaxWordCount}
        </div>
      </div>
      <div className="react-select-parent">
        <label htmlFor="suppliercategory">Category*</label>
        <p>
          Please choose the types of party that you can do! <strong>Maximum of 5</strong>
        </p>
        <div className={styles.reactSelect}>
          <ReactSelect
            styles={reactSelectStyles}
            classNamePrefix="react-select"
            className="react-select-container"
            id="suppliercategory"
            name="suppliercategory"
            required={true}
            noOptionsMessage={noOptionsMessageCategories}
            isMulti
            onChange={handleChangeSelect}
            options={categoryMaxReached ? [] : categoryOptions.filter((el) => !el.virtual)}
            value={categoryOptions.filter((v) => data?.suppliercategory.find((el) => el === v.value))}
          />
          <ReactSelectAlias value={data.suppliercategory} />
        </div>
      </div>
      <div className="react-select-parent">
        <label htmlFor="supplierservices">Service*</label>
        <p>Please select the primary service that best represents your business.</p>
        <div className={styles.reactSelect}>
          <ReactSelect
            styles={reactSelectStyles}
            classNamePrefix="react-select"
            className="react-select-container"
            id="supplierservices"
            name="supplierservices"
            noOptionsMessage={noOptionsMessageServices}
            isMulti
            onChange={handleChangeSelect}
            options={serviceMaxReached ? [] : serviceOptions}
            value={serviceOptions.filter((v) => data.supplierservices?.find((el) => el === v.value))}
          />
          <ReactSelectAlias value={data.supplierservices} />
        </div>
      </div>

      {data.supplierservices.map((service, index) => {
        const serviceData = serviceOptions.filter((serviceOption) => serviceOption.value === service)
        const serviceId = serviceData[0].value
        const subServiceOptionsData = subServiceOptions.filter((item) => service.includes(item.dataService.id))
        const featureOptionsData = featureOptions.filter((item) =>
          item.dataServiceids.includes(service)
        )

        return (
          <div className="service-selectors" key={serviceData[0].value}>
            {subServiceOptionsData.length ? (
              <div className="react-select-parent">
                <p>
                  Please select <strong>up to 5</strong> specific services that best represents your business
                </p>
                <label htmlFor="suppliersubservices">{serviceData[0].label} Specific Services*. </label>
                <div className={styles.reactSelect}>
                  <ReactSelect
                    styles={reactSelectStyles}
                    classNamePrefix="react-select"
                    className="react-select-container"
                    name={"suppliersubservices[" + serviceData[0].value + "]"}
                    isMulti
                    onChange={handleChangeSelect}
                    options={
                      subServiceMaxReached && subServiceMaxReached[serviceId]
                        ? [
                            {
                              label: "You can only select 5 options",
                              value: 0,
                              isDisabled: true,
                            },
                          ]
                        : subServiceOptionsData
                    }
                    value={subServiceOptionsData.filter((v) =>
                      data["suppliersubservices[" + serviceData[0].value + "]"]?.find((el) => el === v.value)
                    )}
                  />
                  <ReactSelectAlias value={data["suppliersubservices[" + serviceData[0].value + "]"]} />
                </div>
              </div>
            ) : null}
            {featureOptionsData.length ? (
              <div className="react-select-parent">
                <label htmlFor="supplierfeature">{serviceData[0].label} Features</label>
                <p>Please select the following features that you provide:</p>
                <CheckboxArray
                  values={featureOptionsData}
                  value={data.supplierfeature}
                  className={"checkbox-wrapper"}
                  name={"supplierfeature"}
                  handleChange={handleChangeCheckboxArray}
                />
              </div>
            ) : null}
            {serviceData[0].label === "Venue" ? (
              <div>
                <div>
                  <label htmlFor="seatedcapacity">Seated Capacity (number only)</label>
                  <input
                    required
                    id="seatedcapacity"
                    name="seatedcapacity"
                    onChange={handleChange}
                    value={data.seatedcapacity}
                  />
                </div>
                <div>
                  <label htmlFor="standingcapacity">Standing Capacity (number only)</label>
                  <input
                    required
                    id="standingcapacity"
                    name="standingcapacity"
                    onChange={handleChange}
                    value={data.standingcapacity}
                  />
                </div>
              </div>
            ) : null}
          </div>
        )
      })}

      <div className="react-select-parent supplierstyle">
        <label htmlFor="supplierstyle">Style*</label>
        <p>
          Please select <strong>up to 3 different styles</strong> that you feel best reflects your business. Again, we
          realise that you may be flexible but please choose the styles that you feel best represent your brand or
          skills
        </p>
        <div className={styles.reactSelect}>
          <ReactSelect
            styles={reactSelectStyles}
            classNamePrefix="react-select"
            className="react-select-container"
            id="supplierstyle"
            name="supplierstyle"
            noOptionsMessage={noOptionsMessageStyles}
            isMulti
            onChange={handleChangeSelect}
            options={styleMaxReached ? [] : styleOptions}
            value={styleOptions.filter((v) => data.supplierstyle?.find((el) => el === v.value))}
          />
          <ReactSelectAlias value={data.supplierstyle} />
        </div>
      </div>
    </section>
  )
}

export default FormDetails
